import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import TextInput from '../../../shared/Inputs/TextInput/TextInput'
import DatePicker from '../../../shared/Inputs/DatePicker/DatePicker'
import { yearsAgo } from '../../../utils/helpers'
import {
  validateDate,
  validateString,
  smartValidateString,
} from '../../../validate/validators'
import { Country, Lang } from '../../../types/common-types'
import { State } from '../../../state-actions-reducers'
import SelectInput from '../../../shared/Inputs/SelectInput/SelectInput'
import PhoneInput from '../../../shared/Inputs/PhoneInput/PhoneInput'
import EmailInput from '../../../shared/Inputs/EmailInput/EmailInput'
import styles from '../ScreenOne.module.scss'
import RussianForm from './Citizenships/RussianForm'
import KyrgyzstanForm from './Citizenships/KyrgyzstanForm'
import RomanianForm from './Citizenships/RomanianForm'
import ChinaForm from './Citizenships/ChinaForm'
import KazakhstanForm from './Citizenships/KazakhstanForm'
import BelarusForm from './Citizenships/BelarusForm'
import UkraineForm from './Citizenships/UkraineForm'
import UzbekistanForm from './Citizenships/UzbekistanForm'
import VietnamForm from './Citizenships/VietnamForm'
import GermanyForm from './Citizenships/GermanyForm'
import UsaForm from './Citizenships/UsaForm'
import TajikistanForm from './Citizenships/TajikistanForm'
import TurkmenistanForm from './Citizenships/TurkmenistanForm'
import TurkishForm from './Citizenships/TurkishForm'
import LitvaForm from './Citizenships/LitvaForm'
import IndiaForm from './Citizenships/IndiaForm'
import SwedenForm from './Citizenships/SwedenForm'
import AddressInputs from '../AddressInputs/AddressInputs'
import AdditionalDocuments from '../AdditionalDocuments/AdditionalDocuments'
import { SexSelect } from '../../../shared/Inputs/SexSelect/SexSelect'
import {
  passportTypeKzForRf,
  passportTypeRf,
  passportTypeRom,
  passportTypeKgz,
  passportTypeGbr,
  passportTypeBelForRf,
  passportTypeUkrForRf,
  passportTypeLitva,
  passportTypeGer,
  passportTypeUsa,
  passportTypeTaj,
  passportTypeTkm,
  passportTypeTur,
  passportTypeUzb,
  DocumentTypeValuesTypePassport,
  passportTypeIndia,
  passportTypeSwe,
  passportTypeNga,
  passportTypeFra,
  passportTypeVnm,
  passportTypeEsp,
  passportTypeArm,
  passportTypePakistan,
  passportTypePrk, passportTypeLatvia,
  passportTypeChn,
  passportTypeGeo,
} from '../../../content/passportType'
import {
  additionalDocumentTypeValuesRf,
  additionalDocumentTypeValuesOthers,
  additionalDocumentTypeValuesKzForRf,
  additionalDocumentTypeValuesBel,
} from '../../../content/additionalDocumentType'
import { rfCity } from '../../../content/cities'
import AddCitizenships from '../../../components/AddCitizenships/AddCitizenships'
import BritishForm from './Citizenships/BritishForm'
import { familyStatusMaleOptions, familyStatusFemaleOptions } from '../../../content/familyStatusType'
import NigeriaForm from './Citizenships/NigeriaForm'
import { COUNTRY, INSTANCE } from '../../../server-parameters'
import useTransliterate from '../../../utils/useTransliterate'
import CheckBoxInput from '../../../shared/Inputs/CheckBoxInput/CheckBoxInput'
import MessengersCheckbox from '../../../shared/MessengersCheckbox/MessengersCheckbox'
import InfoMessage from '../../../shared/InfoMessage/InfoMessage'
import cn from 'classnames'
import FranceForm from './Citizenships/FranceForm'
import SpainForm from './Citizenships/SpainForm'
import ArmeniaForm from './Citizenships/ArmeniaForm'
import PakistanForm from './Citizenships/PakistanForm'
import SouthKoreaForm from './Citizenships/SouthKoreaForm'
import LatviaForm from './Citizenships/LatviaForm'
import IdentityExist from '../../../components/IdentityExist/IdentityExist'
import GeorgiaForm from './Citizenships/GeorgiaForm'
import InnExist from '../../../components/IdentityExist/InnExist'
import { usePhoneEmailCheck } from '../../../hooks/usePhoneEmailCheck'

interface Props {
  lang: Lang
  accountForm: any
  citizenshipCountriesList: Country[]
  allCitizenships: Country[]
  addressCountries: Country[]
  regionCityRegistered: any
  regionCityFactual: any
}

const CITIZENSHIP_PASSPORT_TYPE_MAP: Record<string, any> = {
  'Казахстан': passportTypeKzForRf,
  'Российская Федерация': passportTypeRf,
  'Кыргызстан': passportTypeKgz,
  'Армения': passportTypeArm,
  'Великобритания': passportTypeGbr,
  'Беларусь': passportTypeBelForRf,
  'Украина': passportTypeUkrForRf,
  'Румыния': passportTypeRom,
  'Соединенные Штаты': passportTypeUsa,
  'Турция': passportTypeTur,
  'Нигерия': passportTypeNga,
  'Франция': passportTypeFra,
  'Германия': passportTypeGer,
  'Грузия': passportTypeGeo,
  'Вьетнам': passportTypeVnm,
  'Испания': passportTypeEsp,
  'Узбекистан': passportTypeUzb,
  'Южная Корея': passportTypePrk,
  'Пакистан': passportTypePakistan,
  'Китай': passportTypeChn,
  'Латвия': passportTypeLatvia,
  'Литва': passportTypeLitva,
  'Индия': passportTypeIndia,
  'Швеция': passportTypeSwe,
  'Таджикистан': passportTypeTaj,
  'Туркменистан': passportTypeTkm,
}

type CitizenshipFormProps = { lang: any; accountForm: any }

const CITIZENSHIP_FORM_MAP: Record<string, React.ComponentType<CitizenshipFormProps>> = {
  'Российская Федерация': RussianForm,
  'Беларусь': BelarusForm,
  'Бразилия': BelarusForm,
  'Италия': BelarusForm,
  'Украина': UkraineForm,
  'Кыргызстан': KyrgyzstanForm,
  'Армения': ArmeniaForm,
  'Великобритания': BritishForm,
  'Румыния': RomanianForm,
  'Китай': ChinaForm,
  'Казахстан': KazakhstanForm,
  'Германия': GermanyForm,
  'Соединенные Штаты': UsaForm,
  'Таджикистан': TajikistanForm,
  'Туркменистан': TurkmenistanForm,
  'Турция': TurkishForm,
  'Грузия': GeorgiaForm,
  'Франция': FranceForm,
  'Нигерия': NigeriaForm,
  'Литва': LitvaForm,
  'Индия': IndiaForm,
  'Швеция': SwedenForm,
  'Вьетнам': VietnamForm,
  'Испания': SpainForm,
  'Узбекистан': UzbekistanForm,
  'Пакистан': PakistanForm,
  'Латвия': LatviaForm,
  'Южная Корея': SouthKoreaForm,
  'Болгария': VietnamForm,
  'Израиль': VietnamForm,
  'Исландия': VietnamForm,
  'Молдова, Республика': VietnamForm,
  'Нидерланды': VietnamForm,
  'Норвегия': VietnamForm,
  'Южная Африка': VietnamForm,
  'Индонезия': VietnamForm,
  'Финляндия': VietnamForm,
}

/* REACT COMPONENT */
function ScreenOne({
  lang,
  accountForm,
  citizenshipCountriesList,
  allCitizenships,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
}: Props) {
  const { watch, setValue, formState: { errors } } = useFormContext<State>()
  const state = watch()

  const countriesRef = useRef(
    allCitizenships?.reduce(
      (object, country) => ({
        ...object,
        [country.alpha2 as any]: (country as any)[lang],
      }),
      {}
    )
  )
  const countries = countriesRef.current
  const [focus, setFocus] = useState('')
  const getCitizenshipFormat = (citizenship: string) => {
    return citizenshipCountriesList?.find((country: Country) => {
      return country.value === citizenship
    })?.label?.rus
  }
  const selectedCitizenship = useMemo(() => {
    return getCitizenshipFormat(state.citizenship)
  }, [state.citizenship])

  usePhoneEmailCheck({ state, setValue, accountForm, lang })

  useEffect(() => {
    setValue('mainCity', INSTANCE === 'msk' ? rfCity[1].value : rfCity[0].value, { shouldDirty: true })
  }, [lang])
  useEffect(() => {
    if (state.passportType?.length === 0) {
      const passportOptions = selectedCitizenship ? CITIZENSHIP_PASSPORT_TYPE_MAP[selectedCitizenship] : null
      setValue('passportType',
        passportOptions
          ? String(passportOptions[0].value)
          : String(DocumentTypeValuesTypePassport[0].value),
        { shouldDirty: true }
      )
    }
  }, [selectedCitizenship])
  const [showAddCitizenships, setShowAddCitizenships] = useState(false)

  const CitizenshipForm = selectedCitizenship ? CITIZENSHIP_FORM_MAP[selectedCitizenship] ?? null : null
  const commonProps = { lang, accountForm }

  return (
    <>
      <main className={styles.main}>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepOnePersonalTitle[lang]}</h3>
            </div>
            <div className="col-4 col-xs-12">
              {/* MOBILE PHONE NUMBER */}
              <PhoneInput
                testName="mobilePhoneNumbers"
                testErrorName="error-mobilePhoneNumbers"
                label={accountForm.mobilePhoneNumber.label[lang]}
                lang={lang}
                numbers={
                  state.mobilePhoneNumbers.length === 0
                    ? ['']
                    : state.mobilePhoneNumbers
                }
                setNumbers={data => {
                  setValue('mobilePhoneNumbers', data, { shouldDirty: true })
                  if (!state.Confirm_phone_ok) {
                    setValue('Confirm_phone', data[0] || '', { shouldDirty: true })
                  }
                }}
                invalidText={accountForm?.mobilePhoneNumber?.invalidText?.[lang]}
                countries={countries}
                required
                firstlocked={state.Confirm_phone_ok}
                valid={
                  !errors.mobilePhoneNumbers &&
                  state.isPhoneOk
                }
                phoneCheck={!state.isPhoneOk}
              />
              <MessengersCheckbox lang={lang} accountForm={accountForm} phoneNumber={state.mobilePhoneNumbers[0]} />
            </div>
            <div className="col-6 col-xs-12">
              {/* EMAIL */}
              <EmailInput
                testName="email"
                testErrorName="error-email"
                emails={state.emails.length === 0 ? [''] : state.emails}
                setEmails={data => {
                  setValue('emails', data, { shouldDirty: true })
                  if (!state.Confirm_mail_ok) {
                    setValue('Confirm_mail', data[0] || '', { shouldDirty: true })
                  }
                }}
                placeholder={accountForm?.email?.placeholder?.[lang]}
                invalidText={accountForm?.email?.invalidText?.[lang]}
                invalidTextCorrect={accountForm?.email?.invalidTextCorrect?.[lang]}
                accountForm={accountForm}
                lang={lang}
                required
                firstlocked={state.Confirm_mail_ok}
                valid={!errors.emails}
                mailCheck={state.emails[0]?.length ? !state.isEmailOk : false}
              />
            </div>
            <IdentityExist
              accountForm={accountForm}
              lang={lang}
              show={!state.isPhoneOk || (!!state.emails[0]?.length && !state.isEmailOk)}
            />
            {lang !== 'eng' ? (
              <>
                <div className="col-4 col-xs-12">
                  {/* LAST NAME */}
                  <TextInput
                    testName="lastname"
                    testErrorName="error-lastname"
                    disabled={!state.agreed}
                    lang={lang}
                    content={accountForm.lastName}
                    onChange={lastName => {
                      setValue('lastName', lastName, { shouldDirty: true })
                      if (lang === 'rus' || lang === 'kz') setValue('latinLastname', useTransliterate(lastName), { shouldDirty: true })
                    }}
                    value={state.lastName || ''}
                    required
                    valid={
                      state.lastName.length > 0
                        ? smartValidateString(state.lastName) &&
                        !errors.lastName
                        : !errors.lastName
                    }
                  />
                </div>
                <div className="col-4 col-xs-12">
                  {/* FIRST NAME */}
                  <TextInput
                    testName="firstname"
                    testErrorName="error-firstname"
                    disabled={!state.agreed}
                    lang={lang}
                    content={accountForm.firstName}
                    onChange={firstName => {
                      setValue('firstName', firstName, { shouldDirty: true })
                      if (lang === 'rus' || lang === 'kz') setValue('latinFirstname', useTransliterate(firstName), { shouldDirty: true })
                    }}
                    value={state.firstName || ''}
                    required
                    valid={
                      state.firstName.length > 0
                        ? smartValidateString(state.firstName) &&
                        !errors.firstName
                        : !errors.firstName
                    }
                  />
                </div>
                <div className="col-4 col-xs-12">
                  {/* PATRONYMIC */}
                  <TextInput
                    testName="patronymic"
                    testErrorName="error-patronymic"
                    testCheckName={'noPatronymic'}
                    disabled={!state.agreed || state.noPatronymic}
                    lang={lang}
                    content={accountForm.patronymic}
                    onChange={patronymic => {
                      setValue('patronymic', patronymic, { shouldDirty: true })
                      if (lang === 'rus' || lang === 'kz') setValue('latinPatronymic', useTransliterate(patronymic), { shouldDirty: true })
                    }}
                    checkLabel={accountForm.noPatronymic[lang]}
                    checkValue={state.noPatronymic}
                    checkChange={() => {
                      setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                      setValue('patronymic', '', { shouldDirty: true })
                      setValue('latinPatronymic', '', { shouldDirty: true })
                    }}
                    required
                    value={state.patronymic || ''}
                    valid={
                      state.patronymic.length > 0
                        ? smartValidateString(state.patronymic) &&
                        !errors.patronymic
                        : !errors.patronymic
                    }
                  />
                </div>
              </>
            ) : null}
            <div className="col-4 col-xs-12">
              {/* LATIN LAST NAME */}
              <TextInput
                testName="latinLastname"
                testErrorName="error-latinLastname"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.latinLastname}
                onChange={latinLastname => {
                  latinLastname = latinLastname.replace(
                    /[^A-Za-z0-9\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\'\ ]/gi,
                    ''
                  )
                  setValue('latinLastname', latinLastname, { shouldDirty: true })
                  if (lang === 'eng') setValue('lastName', latinLastname, { shouldDirty: true })
                }}
                value={state.latinLastname || ''}
                required
                valid={
                  state.latinLastname.length > 0
                    ? smartValidateString(state.latinLastname) &&
                    !errors.latinLastname
                    : !errors.latinLastname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* LATIN FIRST NAME */}
              <TextInput
                testName="latinFirstname"
                testErrorName="error-latinFirstname"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.latinFirstname}
                onChange={latinFirstname => {
                  latinFirstname = latinFirstname.replace(
                    /[^A-Za-z0-9\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\'\ ]/gi,
                    ''
                  )
                  setValue('latinFirstname', latinFirstname, { shouldDirty: true })
                  if (lang === 'eng') setValue('firstName', latinFirstname, { shouldDirty: true })
                }}
                value={state.latinFirstname || ''}
                required
                valid={
                  state.latinFirstname.length > 0
                    ? smartValidateString(state.latinFirstname) &&
                    !errors.latinFirstname
                    : !errors.latinFirstname
                }
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* LATIN PATRONYMIC */}
              <TextInput
                testName="latinPatronymic"
                testErrorName="error-latinPatronymic"
                testCheckName={'noPatronymic'}
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.latinPatronymic}
                checkLabel={
                  lang === 'eng' ? accountForm.noPatronymic[lang] : undefined
                }
                checkValue={lang === 'eng' ? state.noPatronymic : false}
                checkChange={() => {
                  if (lang === 'eng') {
                    setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true })
                    setValue('latinPatronymic', '', { shouldDirty: true })
                    setValue('patronymic', '', { shouldDirty: true })
                  }
                }}
                onChange={latinPatronymic => {
                  latinPatronymic = latinPatronymic.replace(
                    /[^A-Za-z0-9\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\'\ ]/gi,
                    ''
                  )
                  setValue('latinPatronymic', latinPatronymic, { shouldDirty: true })
                  if (lang === 'eng') setValue('patronymic', latinPatronymic, { shouldDirty: true })
                }}
                required
                value={state.latinPatronymic || ''}
                valid={
                  state.latinPatronymic.length > 0
                    ? smartValidateString(state.latinPatronymic) &&
                    !errors.latinPatronymic
                    : !errors.latinPatronymic
                }
              />
            </div>
          </div>
          <div className="row">
            <div className="col-4 col-xs-12">
              {/* CITIZENSHIP */}
              <SelectInput
                testName={'citizenship'}
                testErrorName={'error-citizenship'}
                noOptionsMessage={accountForm.citizenshipInput.placeholder}
                lang={lang}
                disabled={!state.agreed}
                content={accountForm.citizenshipInput}
                options={citizenshipCountriesList?.filter((e) => !e.disabled)}
                onChange={citizenship => {
                  setValue('citizenship', citizenship, { shouldDirty: true })
                  const fmt = getCitizenshipFormat(citizenship)
                  const passportOptions = fmt ? CITIZENSHIP_PASSPORT_TYPE_MAP[fmt] : null
                  setValue('passportType',
                    passportOptions
                      ? String(passportOptions[0].value)
                      : String(DocumentTypeValuesTypePassport[0].value),
                    { shouldDirty: true }
                  )
                }}
                valid={!errors.citizenship}
                value={state.citizenship}
                required
                prefix="CitizenshipsRf"
              />
              <span
                className={styles.addCitizenships}
                onClick={() => setShowAddCitizenships(!showAddCitizenships)}
              >
                {accountForm.noCitizenshipsLink[lang]}
              </span>
            </div>
            <div className="col-3 col-xs-12">
              {/* BIRTH DATE */}
              <DatePicker
                testName="birthDate"
                testErrorName="error-birthDate"
                disabled={!state.agreed}
                lang={lang}
                accountForm={accountForm}
                accountFormKey="birthDate"
                onChange={birthDate =>
                  setValue('birthDate', birthDate, { shouldDirty: true })
                }
                value={state.birthDate}
                valid={
                  !errors.birthDate &&
                  validateDate({
                    current: state.birthDate,
                    min: yearsAgo(130),
                    max: yearsAgo(14),
                  })
                }
                curr={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                  )
                }
                min={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 85)
                  )
                }
                max={
                  new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18)
                  )
                }
                required
              />
            </div>
            <div className="col-5 col-xs-12">
              {/* BIRTH PLACE */}
              <TextInput
                testName="birthPlace"
                testErrorName="error-birthPlace"
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.birthPlace}
                onChange={birthPlace => {
                  setValue('birthPlace', birthPlace, { shouldDirty: true })
                }}
                value={state.birthPlace}
                required
                valid={
                  state.birthPlace.length > 0
                    ? smartValidateString(state.birthPlace) &&
                    !errors.birthPlace
                    : !errors.birthPlace
                }
                onBlur={() => setFocus('')}
              />
            </div>
            {showAddCitizenships ? (
              <div className="col-12">
                <AddCitizenships
                  accountForm={accountForm}
                  lang={lang}
                  noContacts={true}
                  handlerChange={() => setShowAddCitizenships(false)}
                />
              </div>
            ) : null}
            <div className="col-4 col-xs-12">
              <SexSelect
                lang={lang}
                accountForm={accountForm}
              />
            </div>
            {/* FAMILY_STATUS */}
            <div className="col-4 col-xs-12">
              <SelectInput
                lang={lang}
                noOptionsMessage={accountForm.familyStatus.placeholder}
                content={accountForm.familyStatus}
                value={state.familyStatus}
                options={state.sex === '2' ? familyStatusMaleOptions : familyStatusFemaleOptions}
                valid={
                  !errors.familyStatus
                }
                onChange={data =>
                  setValue('familyStatus', data, { shouldDirty: true })
                }
                disabled={state.sex === '0'}
                className={styles.input}
              />
            </div>
            <div className="col-12 col-xs-12">
              <InfoMessage type='default' text={accountForm.innMessage[lang]} />
              <div className={cn(styles.inn, "col-4 col-xs-12")}>
                {/* INN */}
                <TextInput
                  disabled={!state.agreed}
                  lang={lang}
                  content={accountForm.passportInnNumber}
                  onChange={inn => {
                    setValue('taxIdentifier', inn.replace(/\D/g, ''), { shouldDirty: true })
                  }}
                  value={state.taxIdentifier}
                  required
                  valid={
                    !errors.taxIdentifier
                  }
                  onBlur={() => setFocus('')}
                  mask={'999999999999'}
                />
                <span className={styles.innSmallMessage}>{accountForm.innSmallMessage[lang]}</span>
              </div>
            </div>
            <InnExist
              accountForm={accountForm}
              lang={lang}
              show={!state.isIdentityOk}
            />
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-4 col-xs-12">
              <SelectInput
                testName={'passportType'}
                testErrorName={'error-passportType'}
                lang={lang}
                noOptionsMessage={accountForm.passportTypeInput.placeholder}
                options={
                  selectedCitizenship
                    ? (CITIZENSHIP_PASSPORT_TYPE_MAP[selectedCitizenship] ?? DocumentTypeValuesTypePassport)
                    : DocumentTypeValuesTypePassport
                }
                content={accountForm.passportTypeInput}
                onChange={data => {
                  setValue('passportType', data, { shouldDirty: true })
                }}
                value={state.passportType}
                valid={
                  !errors.passportType &&
                  validateString(state.passportType, 300, true)
                }
                required
              />
            </div>
            {CitizenshipForm && <CitizenshipForm {...commonProps} />}
          </div>
        </div>
        <AddressInputs
          lang={lang}
          accountForm={accountForm}
          addressCountries={addressCountries}
          regionCityRegistered={regionCityRegistered}
          regionCityFactual={regionCityFactual}
        />
        <AdditionalDocuments
          lang={lang}
          accountForm={accountForm}
          selectedCitizenship={selectedCitizenship}
          documentsTypeValues={
            selectedCitizenship === 'Казахстан'
              ? additionalDocumentTypeValuesKzForRf
              : selectedCitizenship === 'Российская Федерация'
                ? additionalDocumentTypeValuesRf
                : selectedCitizenship === 'Беларусь' ?
                  additionalDocumentTypeValuesBel :
                  additionalDocumentTypeValuesOthers
          }
        />
      </main>
    </>
  )
}

export default ScreenOne
